import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { Capacitor } from '@capacitor/core'
import { AuthLayout } from '@/components/common/AuthLayout'
import { Layout } from '@/components/common/Layout'
import { MarketingLayout } from '@/components/marketing/MarketingLayout'
import { AuthGuard, GuestGuard } from '@/components/auth'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ChildrenListPage } from '@/pages/ChildrenListPage'
import { AddChildPage } from '@/pages/AddChildPage'
import { ChildDetailPage } from '@/pages/ChildDetailPage'
import { UpdatesPage } from '@/pages/UpdatesPage'
import { ParentTimelinePage } from '@/pages/ParentTimelinePage'
import { ParentChildDetailPage } from '@/pages/ParentChildDetailPage'
import { NotificationSettingsPage } from '@/pages/NotificationSettingsPage'
import { ChildminderSettingsPage } from '@/pages/ChildminderSettingsPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { CalendarPage } from '@/pages/CalendarPage'
import { AttendancePage } from '@/pages/AttendancePage'
import { ParentCalendarPage } from '@/pages/ParentCalendarPage'
import { MessagesPage } from '@/pages/MessagesPage'
import { ConversationPage } from '@/pages/ConversationPage'
import { NewConversationPage } from '@/pages/NewConversationPage'
import { ProfileSettingsPage } from '@/pages/ProfileSettingsPage'
import { InvitationPage } from '@/pages/InvitationPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage'
import { TermsOfServicePage } from '@/pages/TermsOfServicePage'
import { CookieSettingsPage } from '@/pages/CookieSettingsPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { HomePage } from '@/pages/marketing/HomePage'
import { FeaturesPage } from '@/pages/marketing/FeaturesPage'
import { PricingPage } from '@/pages/marketing/PricingPage'

// Check if running on native platform (iOS/Android)
const isNative = Capacitor.isNativePlatform()

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Marketing Routes - WEB ONLY */}
        {!isNative && (
          <Route element={<MarketingLayout />}>
            <Route index element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
          </Route>
        )}

        {/* Native app: redirect "/" to dashboard */}
        {isNative && (
          <Route index element={<Navigate to="/dashboard" replace />} />
        )}

        {/* Public auth routes - redirect to dashboard if already logged in */}
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={
              <GuestGuard>
                <LoginPage />
              </GuestGuard>
            }
          />
          <Route
            path="/register"
            element={
              <GuestGuard>
                <RegisterPage />
              </GuestGuard>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <GuestGuard>
                <ForgotPasswordPage />
              </GuestGuard>
            }
          />
          <Route
            path="/reset-password"
            element={
              <GuestGuard>
                <ResetPasswordPage />
              </GuestGuard>
            }
          />
        </Route>

        {/* Protected app routes */}
        <Route element={<Layout />}>
          <Route
            path="/dashboard"
            element={
              <AuthGuard>
                <DashboardPage />
              </AuthGuard>
            }
          />
          {/* Childminder routes */}
          <Route
            path="/children"
            element={
              <AuthGuard allowedRoles={['childminder']}>
                <ChildrenListPage />
              </AuthGuard>
            }
          />
          <Route
            path="/children/new"
            element={
              <AuthGuard allowedRoles={['childminder']}>
                <AddChildPage />
              </AuthGuard>
            }
          />
          <Route
            path="/children/:id"
            element={
              <AuthGuard allowedRoles={['childminder']}>
                <ChildDetailPage />
              </AuthGuard>
            }
          />
          <Route
            path="/updates"
            element={
              <AuthGuard allowedRoles={['childminder']}>
                <UpdatesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/calendar"
            element={
              <AuthGuard allowedRoles={['childminder']}>
                <CalendarPage />
              </AuthGuard>
            }
          />
          <Route
            path="/attendance"
            element={
              <AuthGuard allowedRoles={['childminder']}>
                <AttendancePage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthGuard allowedRoles={['childminder']}>
                <ChildminderSettingsPage />
              </AuthGuard>
            }
          />
          {/* Parent routes */}
          <Route
            path="/timeline"
            element={
              <AuthGuard allowedRoles={['parent']}>
                <ParentTimelinePage />
              </AuthGuard>
            }
          />
          <Route
            path="/my-calendar"
            element={
              <AuthGuard allowedRoles={['parent']}>
                <ParentCalendarPage />
              </AuthGuard>
            }
          />
          <Route
            path="/my-children/:id"
            element={
              <AuthGuard allowedRoles={['parent']}>
                <ParentChildDetailPage />
              </AuthGuard>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <AuthGuard allowedRoles={['parent']}>
                <NotificationSettingsPage />
              </AuthGuard>
            }
          />
          {/* Messages routes - available to both roles */}
          <Route
            path="/messages"
            element={
              <AuthGuard>
                <MessagesPage />
              </AuthGuard>
            }
          />
          <Route
            path="/messages/new"
            element={
              <AuthGuard>
                <NewConversationPage />
              </AuthGuard>
            }
          />
          <Route
            path="/messages/:id"
            element={
              <AuthGuard>
                <ConversationPage />
              </AuthGuard>
            }
          />
          {/* Profile settings - available to both roles */}
          <Route
            path="/profile"
            element={
              <AuthGuard>
                <ProfileSettingsPage />
              </AuthGuard>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Public invitation route */}
        <Route path="/invite/:token" element={<InvitationPage />} />

        {/* Email verification route */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Legal pages - public */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/cookies" element={<CookieSettingsPage />} />
      </Routes>
    </BrowserRouter>
  )
}
